/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 *
 */
import React from 'react'
import _ from 'lodash'
import r from '../../../util/Renderer'
import { dateToParam, dateToStringDate, dateToStringDateWithoutYear } from '../../../util/DateString'
import { calcStayingDays } from '../../../util/DateProcessing'
import { Dropdown, OtaLogo, Tag } from '../../atoms'
import { ReservationDetail } from '../../organisms/reservations'
import ReservationAction from '../../../action/reservation'
import ReceptionistAction from '../../../action/reservation/receptionist'
import {
  List,
  ListRow,
  ListRowItem,
  MdcChip,
  MdcChipSet,
  NavigationDrawer,
  NormalButton,
  OutlinedButton,
  SearchCheckbox,
  SearchField,
  SearchOption,
  ToolbarDateNavigation
} from 'ui-library/dist/external/components'
import { BaseLayout, ListViewLayout, LocalAreaLayout } from 'ui-library/dist/external/layouts'
import { DeviceType } from 'ui-library/dist/external/constants'
import Money from '../../unit/Money'
import { PaymentPaid } from '../../parts/Payment'
import { NoShow } from '../../parts/Reservation'

function ReceptionistView(props) {
  const date = props.search.search_date
  let prevDate = _.cloneDeep(date)
  prevDate.setDate(date.getDate() - 1)
  let nextDate = _.cloneDeep(date)
  nextDate.setDate(date.getDate() + 1)

  const paginations = {
    prev: {
      onClick: e => {
        ReservationAction.dateChange(prevDate)
      }
    },
    next: {
      onClick: e => {
        ReservationAction.dateChange(nextDate)
      }
    },
    calendar: {
      onClick: e => {},
      onDateClick: (y, m, d) => {
        const calendarDate = new Date(y, m - 1, d)
        ReservationAction.dateChange(calendarDate)
      },
      selectedDate: date
    }
  }

  const meta = {
    label: '',
    component: ToolbarDateNavigation,
    data: { label: dateToStringDate(date), paginations: paginations }
  }

  const toolbar = { label: '本日の宿泊予定', items: props.accountMenu }

  return (
    <>
      <NavigationDrawer toolbar={toolbar} navigations={props.navigations} meta={meta} />
      <BaseLayout>
        <LocalAreaLayout
          left={
            <SearchField
              name="search_keywords"
              value={props.search.search_keywords}
              onChange={e => ReceptionistAction.onEditSearchCondition(e.target.name, e.target.value)}
              onSearch={e => ReceptionistAction.onSearch(props.search)}
              searchOptions={[
                <SearchOption
                  key={1}
                  label={'宿泊施設'}
                  field={
                    <Dropdown
                      name="search_hotel_ids"
                      value={props.search.search_hotel_ids}
                      options={props.search.search_hotel_options}
                      isClearable={true}
                      isMulti={true}
                      searchable={true}
                      onChange={e => ReceptionistAction.onEditSearchCondition(e.target.name, e.target.value)}
                    />
                  }
                />,
                <SearchOption
                  key={2}
                  label={'ステータス'}
                  field={
                    <ul>
                      <li>
                        <SearchCheckbox
                          label={'チェックイン'}
                          name={'search_status_checkin'}
                          checked={props.search.search_status_checkin}
                          onChange={e => ReceptionistAction.onEditSearchCondition(e.target.name, e.target.checked)}
                        />
                      </li>
                      <li>
                        <SearchCheckbox
                          label={'チェックアウト'}
                          name={'search_status_checkout'}
                          checked={props.search.search_status_checkout}
                          onChange={e => ReceptionistAction.onEditSearchCondition(e.target.name, e.target.checked)}
                        />
                      </li>
                      <li>
                        <SearchCheckbox
                          label={'滞在中'}
                          name={'search_status_staying'}
                          checked={props.search.search_status_staying}
                          onChange={e => ReceptionistAction.onEditSearchCondition(e.target.name, e.target.checked)}
                        />
                      </li>
                    </ul>
                  }
                />
              ]}
            />
          }
          right={<NormalButton onClick={() => ReservationAction.downloadCsv(props.reservations, props.session.isOwner)}>CSVダウンロード</NormalButton>}
          bottom={ShowSearchCondition(props.search)}
        />
        <ListViewLayout>
          {/* TODO: オーナーが予約情報を編集しないよう一時的な対応 */}
          {/* TODO: キャンセルされた予約を見たいと言われたので一時対応 */}
          <List>
            {r.renderIf(props.reservations)(
              props.reservations.map((v, idx) => (
                <ListItem key={idx} onClick={e => ReservationAction.showDetailView(v.id, `/receptionist/${dateToParam(date)}/${v.id}/detail`)}>
                  {ReservationRowItem(v, props.session.isOwner)}
                </ListItem>
              ))
            )}
          </List>
        </ListViewLayout>

        {r.renderIf(props.showDetail)(<ReservationDetail page={`/receptionist/${dateToParam(date)}`}>{props}</ReservationDetail>)}
      </BaseLayout>
    </>
  )
}

// ---------------------------------------------------------------------------------------------------------------------
//   View
// ---------------------------------------------------------------------------------------------------------------------
function ShowSearchCondition(search) {
  //
  let labels = []
  if (search.search_keywords) {
    labels.push(`キーワード: ${search.search_keywords}`)
  }
  if (search.search_hotel_ids && search.search_hotel_ids.length > 0) {
    let hotelLabel = `宿泊施設: ${search.search_hotel_ids.length} 施設`
    labels.push(hotelLabel)
  }

  if (search.search_status_checkin || search.search_status_checkout || search.search_status_staying) {
    let statusLabel = 'ステータス: '
    let statusValues = []
    if (search.search_status_checkin) {
      statusValues.push('チェックイン')
    }
    if (search.search_status_checkout) {
      statusValues.push('チェックアウト')
    }
    if (search.search_status_staying) {
      statusValues.push('滞在中')
    }
    const status = statusLabel + statusValues.join(', ')
    labels.push(status)
  }

  return (
    <MdcChipSet>
      {labels.map((label, index) => (
        <MdcChip key={label} id={index} label={label} />
      ))}
    </MdcChipSet>
  )
}

function ListItem(props) {
  return <ListRow onClick={props.onClick}>{props.children}</ListRow>
}

function Status(props) {
  const status = props.reservation.status
  const labels = {
    check_in: 'チェックイン',
    check_out: 'チェックアウト',
    staying: '滞在中',
    canncelled: 'キャンセル'
  }
  return <Tag id={status}>{labels[status] || ''}</Tag>
}

function ReservationRowItem(reservation, isOwner) {
  let schedule = ''
  if (reservation && reservation.check_in) {
    schedule = `${calcStayingDays(reservation.check_in, reservation.check_out)}泊 [${dateToStringDateWithoutYear(reservation.check_in)}→${dateToStringDateWithoutYear(reservation.check_out)}]`
  }

  const guest_arrival_time = reservation.guest_arrival_time || '(到着予定記載なし)'
  const tax = reservation.accommodation_tax
  const accommodation_tax = tax ? Math.ceil(tax.tax_per_day * (tax.number_of_nights * tax.number_of_guests)) : null
  return (
    <React.Fragment>
      {r.renderIfOrElse(!reservation.noshow)(
        <ListRowItem
          width={'220px'}
          widthForPhone={'100%'}
          widthForLargeDesktop={'17%'}
          overline={<Status reservation={reservation} />}
          primaryText={reservation.name}
          secondaryText={guest_arrival_time}
        />
      )(
        <ListRowItem
          width={'220px'}
          widthForPhone={'100%'}
          widthForLargeDesktop={'17%'}
          overline={
            <React.Fragment>
              <Status reservation={reservation} />
              &nbsp;
              <NoShow>ノーショー</NoShow>
            </React.Fragment>
          }
          primaryText={reservation.name}
          secondaryText={guest_arrival_time}
        />
      )}

      <ListRowItem width={'280px'} widthForPhone={'100%'} widthForLargeDesktop={'22%'} primaryText={reservation.stay} secondaryText={schedule} />

      <ListRowItem
        width={'120px'}
        showDeviceFor={DeviceType.LARGE_DESKTOP}
        widthForLargeDesktop={'12%'}
        overline={'宿泊料金'}
        primaryItem={
          <React.Fragment>
            <AccommodationFeePaymentStatus reservation={reservation} />
            <Money>{reservation.accommodation_fee}</Money>
          </React.Fragment>
        }
      />
      {r.renderIf(!isOwner)(
        <ListRowItem
          width={'120px'}
          showDeviceFor={DeviceType.LARGE_DESKTOP}
          widthForLargeDesktop={'8%'}
          overline={'宿泊税'}
          primaryItem={
            <React.Fragment>
              <AccommodationTaxPaymentStatus reservation={reservation} />
              <Money>{accommodation_tax}</Money>
            </React.Fragment>
          }
        />
      )}
      <ListRowItem
        width={'150px'}
        showDeviceFor={DeviceType.DESKTOP}
        widthForLargeDesktop={'12%'}
        overline={'人数'}
        secondaryText={`大人 ${reservation.num_adult}名 子供 ${reservation.num_child}名 幼児 ${reservation.num_infant}名`}
      />
      <ListRowItem
        width={'150px'}
        showDeviceFor={DeviceType.LARGE_DESKTOP}
        widthForLargeDesktop={'10%'}
        overline={`${reservation.getMealCondition() || '食事なし'} `}
        primaryText={reservation.meal_delivery || ''}
      />
      <ListRowItem width={'65px'} showDeviceFor={DeviceType.TABLET} widthForLargeDesktop={'6%'} primaryItem={<OtaLogo value={reservation.site_referer} />} />
      {r.renderIf(!isOwner)(
        <ListRowItem
          width={'130px'}
          widthForPhone={'100%'}
          widthForLargeDesktop={'11%'}
          primaryItem={<CheckInButton reservation={reservation} />}
          secondaryItem={
            <CheckinStatusLabel
              checkin_status={reservation.checkin_status}
              form_count={reservation.form_count}
              number_of_guests={countNumberOfGuests(reservation.num_adult, reservation.num_child, reservation.num_infant)}
            />
          }
        />
      )}
    </React.Fragment>
  )
}

function AccommodationFeePaymentStatus(props) {
  const r = props.reservation

  if (!r.is_paid_accommodation_fee) {
    return null
  }

  return <PaymentPaid paid={true} />
}

function AccommodationTaxPaymentStatus(props) {
  const r = props.reservation

  if (!r.is_paid_accommodation_tax) {
    return null
  }

  return <PaymentPaid paid={true} />
}

function PaymentStatus(props) {
  const r = props.reservation
  const fee = r.is_paid_accommodation_fee ? '料金支払済' : ''
  const tax = r.is_paid_accommodation_tax ? ' 税支払済' : ''

  return (
    <small>
      {fee}
      {tax}
    </small>
  )
}

function CheckinStatusLabel(props) {
  const style = {
    width: '116px',
    marginTop: '4px',
    textAlign: 'center'
  }

  const textStyle = {
    color: '#707070',
    fontSize: '0.75rem',
    WebkitFontSmoothing: 'antialiased',
    lineHeight: '1.25rem',
    fontWeight: '400',
    letterSpacing: '0.03333em'
  }

  const form_count = props.form_count || 0
  const number_of_guests = props.number_of_guests || 0
  switch (props.checkin_status) {
    case 'not_yet':
      return ''
    case 'only_checkin':
      return (
        <div style={style}>
          <span style={textStyle}>帳簿未記入</span>
        </div>
      )
    case 'form_wrote':
      return (
        <div style={style}>
          <span style={textStyle}>
            帳簿記入済({form_count}/{number_of_guests})
          </span>
        </div>
      )
    case 'no_show':
      return (
        <div style={style}>
          <span style={textStyle}>ノーショウ</span>
        </div>
      )
    case 'checkin_completed':
      return (
        <div style={style}>
          <span style={textStyle}>完了</span>
        </div>
      )
    default:
      return ''
  }
}

function CheckInButton(props) {
  const checkin_status = props.reservation.checkin_status
  const style = {
    display: 'inline-block',
    width: '108px',
    height: '34px',
    objectFit: 'contain',
    backgroundSize: 'contain',
    border: 'none',
    background: "url('/client_assets/icon/icon-checked-in.svg') no-repeat",
    backgroundPosition: 'center center'
  }

  if (checkin_status === 'checkin_completed') {
    return <div style={style} />
  }

  if (checkin_status === 'only_checkin') {
    return <div style={style} />
  }

  const reservation = props.reservation
  const formCount = reservation.form_count
  const guestCount = countNumberOfGuests(reservation.num_adult, reservation.num_child, reservation.num_infant)

  return <OutlinedButton onClick={e => ReservationAction.confirmCheckIn(e, reservation.id, reservation.name, formCount, guestCount)}>チェックイン</OutlinedButton>
}

function countNumberOfGuests(num_adult, num_child, num_infant) {
  const number_of_adult = num_adult || 0
  const number_of_child = num_child || 0
  const number_of_infant = num_infant || 0

  return number_of_adult + number_of_child + number_of_infant
}

export default ReceptionistView
